import { FC, useEffect } from 'react'
import { InvoicingAddressCard, NewInvoicingAddressCard } from '../../InstructionListing/instructionCards/InvoicingAddressCard'
import { usePFInvoicingAddresses, useTargetOrderUser } from '../../../_main/contexts'

import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { Stack } from '@mui/material'
import { useAuth0 } from 'utils/auth'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Controller component responsible for rendering and managing the
 * Invoicing Address selection step within the Purchase Flow.
 *
 * @remarks
 * - Displays a list of existing invoicing addresses.
 * - Allows selection of an existing address.
 * - Provides an option to create and select a new address.
 * - Automatically defaults to the new address form if no addresses exist.
 * - Handles both client and admin roles:
 *   - Clients can create and select their own addresses.
 *   - Admins can create and select addresses on behalf of another user.
 */
export const InvoicingAddressSectionController: FC = () => {

  const { targetUser } = useTargetOrderUser()
  const { baseUserData } = useUserData()

  /** Auth0 roles of currently logged user */
  const { roles } = useAuth0()

  /** Translation function scoped to purchase flow */
  const { t } = useTranslation(['purchase_flow'])

  const {
    invoicingAddressList,
    selectedInvoicingAddress,
    createInvoicingAddressMutation,
    createInvoicingAddressForUserMutation,
    normalizedListQuery,
    setSelectedInvoicingAddress,
    saveAndUseInvoicingAddress,
    saveAndUseInvoicingAddressForUser,
  } = usePFInvoicingAddresses()

  useEffect(
    () => {
      // Auto-select 'new' only when the query has resolved to an empty list AND
      // the user hasn't already made a selection (don't clobber an existing pick
      // if the list reloads after a "Save and use" adds a new address).
      if (normalizedListQuery.isSuccess && invoicingAddressList.length === 0 && !selectedInvoicingAddress) {
        setSelectedInvoicingAddress('new')
      }
    },
    // Depend on isSuccess + list length so the effect re-evaluates if the query
    // resolves after mount (race condition on slow networks — BX-F04 / CCb-8).
    [normalizedListQuery.isSuccess, invoicingAddressList.length, selectedInvoicingAddress, setSelectedInvoicingAddress]
  )

  return (
    <SectionedBorderBox
      title={t('billing_step.invoicing_address_section_title')}
    >
      <Stack gap={2}>

        {invoicingAddressList.map((address) => (
          <InvoicingAddressCard
            key={address.id}
            address={address}
            checked={selectedInvoicingAddress === address.id}
            onCheck={() => setSelectedInvoicingAddress(address.id)}
          />
        ))}

        {roles.isClient &&
          <NewInvoicingAddressCard
            checked={selectedInvoicingAddress === 'new'}
            mutation={createInvoicingAddressMutation}
            onCheck={() => setSelectedInvoicingAddress('new')}
            onConfirmAddress={(payload) => saveAndUseInvoicingAddress(payload)}
          />
        }

        {roles.isAdmin &&
          <NewInvoicingAddressCard
            checked={selectedInvoicingAddress === 'new'}
            mutation={createInvoicingAddressForUserMutation}
            onCheck={() => setSelectedInvoicingAddress('new')}
            onConfirmAddress={(payload) => saveAndUseInvoicingAddressForUser(targetUser.id!, payload)}
          />
        }

      </Stack>
    </SectionedBorderBox>
  )
}
